import { z } from 'zod';

/**
 * First Valuation (multi-party blind estimate) DTOs.
 *
 * Mirrors `backend/src/modules/first_valuations/model.rs` (SPEC v1.0 Phase 1,
 * Slice 1A). All amount fields are rust_decimal serialized with `serde-with-str`
 * → JSON **strings** (never coerced to number here, matching the kg convention).
 *
 * ONE COMPANY, ONE VALUATION. Role does not enter into it: the platform
 * operator, the processor, and any outside sales company granted an estimating
 * account each file one. The platform operator additionally reviews and
 * releases.
 *
 * BLIND ISOLATION: the server nulls out `valuation_amount` /
 * `material_price_reference` on every submission but the caller's own, plus
 * `middle_value` and the whole `comparison` block, until the valuation is
 * CONFIRMED. Filing reveals nothing. The one exception is a reviewer
 * (`estimation.final_decide`), who must see the figures to choose one. The
 * frontend renders exactly what the server returns — it never tries to reveal
 * a hidden amount.
 */

export const firstValuationStatusValues = [
  'open',
  // Legacy enum label; it now means "a valuation has been filed".
  'both_submitted',
  'finalized',
  'locked',
  'revaluation_requested',
] as const;
export type FirstValuationStatus = (typeof firstValuationStatusValues)[number];

/** Display label for a submission row — never a rule. */
export const estimatorRoleValues = ['platform', 'processor', 'external'] as const;
export type EstimatorRole = (typeof estimatorRoleValues)[number];

export const firstValuationModeValues = ['single_trip', 'weekly_batch'] as const;
export type FirstValuationMode = (typeof firstValuationModeValues)[number];

/** 'company' adopts one named company's figure; 'middle' averages all filed. */
export const finalBasisValues = ['middle', 'company', 'custom'] as const;
export type FinalBasis = (typeof finalBasisValues)[number];

/** Band derived server-side from the difference rate. */
export const differenceBandValues = [
  'normal',
  'warning',
  'abnormal',
  'revaluation_suggested',
] as const;
export type DifferenceBand = (typeof differenceBandValues)[number];

/** One inbound unit to be priced, with the evidence needed to price it. */
export const valuationPackageSchema = z.object({
  inbound_package_id: z.string(),
  inbound_lot_id: z.string(),
  lot_code: z.string(),
  package_seq: z.number(),
  package_code: z.string(),
  raw_incoming_item_name: z.string(),
  net_kg: z.string().nullable().optional(),
  gross_kg: z.string().nullable().optional(),
  inbound_date: z.string(),
  /** This company's estimate; null until the unit is priced. */
  valuation_amount: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  photo_evidence_file_ids: z.array(z.string()).default([]),
});

export type ValuationPackage = z.infer<typeof valuationPackageSchema>;

/** One company's valuation as seen by the caller. */
export const submissionSchema = z.object({
  /** Legacy 'ef'/'dk' marker on historical rows; null for company-keyed ones. */
  estimator_side: z.string().nullable().optional(),
  estimator_company_id: z.string().nullable().optional(),
  estimator_company_name: z.string().nullable().optional(),
  estimator_role: z.enum(estimatorRoleValues).nullable().optional(),
  is_submitted: z.boolean(),
  submitted_by: z.string().nullable().optional(),
  submitted_at: z.string().nullable().optional(),
  /** Hidden (null) until confirmed, except for the caller's own company. */
  valuation_amount: z.string().nullable().optional(),
  /** Hidden (null) until confirmed, except for the caller's own company. */
  material_price_reference: z.unknown().nullable().optional(),
});
export type SubmissionDto = z.infer<typeof submissionSchema>;

/** The spread across every company that filed — same visibility gate. */
export const comparisonSchema = z.object({
  submitted_count: z.number(),
  min_amount: z.string(),
  max_amount: z.string(),
  middle_value: z.string(),
  /** (max - min) / middle, as a fraction (0.10 == 10%). Null if middle is 0. */
  difference_rate: z.string().nullable().optional(),
  difference_band: z.enum(differenceBandValues).nullable().optional(),
});
export type ComparisonDto = z.infer<typeof comparisonSchema>;

export const firstValuationSchema = z.object({
  first_valuation_id: z.string(),
  monthly_batch_id: z.string(),
  mode: z.enum(firstValuationModeValues),
  valuation_code: z.string().nullable().optional(),
  status: z.enum(firstValuationStatusValues),
  /** Hidden (null) until confirmed (reviewers excepted). */
  middle_value: z.string().nullable().optional(),
  final_value: z.string().nullable().optional(),
  final_basis: z.enum(finalBasisValues).nullable().optional(),
  /** Whose valuation was adopted, when `final_basis = 'company'`. */
  final_company_id: z.string().nullable().optional(),
  final_reason: z.string().nullable().optional(),
  finalized_by: z.string().nullable().optional(),
  finalized_at: z.string().nullable().optional(),
  locked_at: z.string().nullable().optional(),
  /** At least one company has filed — enough for the platform to confirm. */
  any_submitted: z.boolean(),
  submissions: z.array(submissionSchema),
  /** Same visibility gate as `middle_value`. */
  comparison: comparisonSchema.nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type FirstValuationDto = z.infer<typeof firstValuationSchema>;

// ---- request bodies (snake_case wire format) -------------------------------

/** POST /api/monthly-batches/:batchId/first-valuations */
export interface CreateFirstValuationInput {
  mode: FirstValuationMode;
  valuation_code?: string;
}

/** POST /api/first-valuations/:id/submit — files for the caller's company. */
export interface SubmitInput {
  valuation_amount: string;
  material_price_reference?: unknown;
}

/** POST /api/first-valuations/:id/finalize */
export interface FinalizeInput {
  final_basis: FinalBasis;
  final_company_id?: string;
  final_value?: string;
  final_reason?: string;
}

/** The caller's own company's submission, if they are an estimator here. */
export const ownSubmission = (
  dto: FirstValuationDto | undefined,
  companyId: string | null | undefined,
): SubmissionDto | undefined =>
  companyId == null
    ? undefined
    : dto?.submissions.find((s) => s.estimator_company_id === companyId);

/**
 * Resolve what the final value WOULD be for a chosen basis, given the
 * comparison. Returns null when the basis needs data we do not have yet
 * (e.g. custom with no input, or comparison not populated). Used client-side
 * to decide whether `final_reason` is required (final differs from middle).
 */
export const resolveFinalValue = (
  basis: FinalBasis,
  comparison: ComparisonDto | null | undefined,
  customValue: string,
  adoptedAmount?: string | null,
): string | null => {
  if (basis === 'custom') return customValue.trim() === '' ? null : customValue.trim();
  if (basis === 'company') return adoptedAmount ?? null;
  if (!comparison) return null;
  return comparison.middle_value;
};

/** True when the resolved final value diverges from the midpoint. */
export const finalDiffersFromMiddle = (
  resolvedFinal: string | null,
  comparison: ComparisonDto | null | undefined,
): boolean => {
  if (resolvedFinal == null || !comparison) return false;
  return Number(resolvedFinal) !== Number(comparison.middle_value);
};
